import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, getAttribution, trackEvent } from "../../lib/api";
import { COLORS, GLAZING, HOME_TYPES, PRODUCTS, QUANTITIES, STEPS, TIMELINES, isDoor, postcodeError, productFromOnderwerp, usesColor, usesGlass, usesQuantity, validateContact } from "../wizard/wizardOptions";
import { ContactStep, FotoStep, ProductStep, WensenStep, WoningStep } from "../wizard/WizardSteps";
import { useWizardCatalog } from "../wizard/useWizardCatalog";
import { IsdeLinked } from "../wizard/IsdeLinked";
import { ReferenceLinked, useWizardReference } from "../wizard/ReferenceLinked";
import { getCity } from "../../data/cities";
import { ISDE_PREFILL_KEY, readIsdePrefill } from "../../lib/isde";

export const QUOTE_DONE_KEY = "mk_quote_done";
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

const EMPTY = { product: null, home_type: null, quantity: null, postcode: "", woonplaats: "", color: null, glazing: null,
  timeline: null, dimensions: "", notes: "", naam: "", email: "", telefoon: "", website: "", door_model: null, door_glass: null, door_glass_type: null, profile_system: null,
  operation: null, roof_situation: null };

const valid = (list, v) => (list.some((o) => o.id === v) ? v : null);
// Antwoorden uit de ISDE-subsidiecheck die de wizard direct overneemt.
const fromIsde = (p) => (p ? { product: valid(PRODUCTS, p.product), glazing: valid(GLAZING, p.glazing), quantity: valid(QUANTITIES, p.quantity),
  home_type: valid(HOME_TYPES, p.home_type), timeline: valid(TIMELINES, p.timeline), dimensions: p.dimensions || "", woonplaats: p.woonplaats || "" } : {});

const apiError = (err) => {
  const d = err?.response?.data?.detail;
  if (err?.response?.status === 429) return "Te veel pogingen achter elkaar. Probeer het over een minuut opnieuw.";
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, "");
  return "Versturen is niet gelukt. Controleer uw verbinding en probeer het opnieuw, of bel ons.";
};

const buildPayload = (form, uploads, meta, isdeId, reference) => {
  const p = form.product;
  const details = Object.fromEntries(Object.entries({ home_type: form.home_type, quantity: usesQuantity(p) ? form.quantity : null,
    color_preference: usesColor(p) ? form.color : null, glazing_preference: usesGlass(p) ? form.glazing : null, timeline: form.timeline, dimensions_approx: form.dimensions.trim(),
    door_model: isDoor(form.product) ? form.door_model : null, door_glass: isDoor(form.product) ? form.door_glass : null,
    door_glass_type: isDoor(form.product) && form.door_glass === "ja" ? form.door_glass_type : null,
    operation: p === "rolluiken" ? form.operation : null, roof_situation: p === "velux_dakramen" ? form.roof_situation : null,
    profile_system: form.profile_system }).filter(([, v]) => v));
  const attr = getAttribution();
  return {
    idempotency_key: meta.key, lead_type: "quote_request", product_category: form.product || "anders", details,
    postal_code: form.postcode.trim(), locality: form.woonplaats.trim(), notes: form.notes.trim(),
    contact_name: form.naam.trim(), email: form.email.trim(), phone: form.telefoon.trim(), consent_privacy: true,
    marketing_opt_in: false, uploaded_files: uploads.filter((u) => u.path).map((u) => u.path), utm: attr.utm || {},
    referrer: attr.referrer || "", landing_page: attr.landing_page || "", source_page: meta.source, website: form.website,
    elapsed_ms: Date.now() - meta.startedAt, isde_check_id: isdeId || "", reference: reference || "",
  };
};

const Progress = ({ step }) => (
  <div className="mb-7" data-testid="wizard-progress">
    <div className="flex items-center justify-between text-[12.5px] font-semibold">
      <span className="uppercase tracking-[0.14em] text-gold">Stap {step} van {STEPS.length}</span>
      <span className="text-slate-500" data-testid="wizard-step-label">{STEPS[step - 1].short}</span>
    </div>
    <ol className="mt-3 grid grid-cols-5 gap-1.5" aria-hidden="true">
      {STEPS.map((s, i) => <li key={s.short} className={`h-1.5 rounded-full transition-colors duration-300 ${i < step ? "bg-maas" : "bg-slate-200"}`} />)}
    </ol>
  </div>
);

export const ProjectAdvies = ({ bronPagina = "" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const topRef = useRef(null);
  const meta = useRef({ key: newKey(), startedAt: Date.now(), source: bronPagina || location.pathname });
  const [params] = useState(() => Object.fromEntries(new URLSearchParams(location.search)));
  const [isde, setIsde] = useState(() => (params.bron === "isde" && /^ISDE-[A-F0-9]{8}$/.test(readIsdePrefill()?.isde_check_id || "") ? readIsdePrefill() : null));
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    const pre = fromIsde(isde);
    return { ...EMPTY, ...Object.fromEntries(Object.entries(pre).filter(([, v]) => v)), product: productFromOnderwerp(params.onderwerp) || pre.product || null,
      woonplaats: getCity(params.plaats)?.name || (params.woonplaats || "").slice(0, 80) || pre.woonplaats || "" };
  });
  const [uploads, setUploads] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { door, systemOptions } = useWizardCatalog(form, setForm, params);
  const [refItem, clearRef] = useWizardReference(params);
  useEffect(() => { if (refItem?.color) setForm((f) => (f.color ? f : { ...f, color: refItem.color })); }, [refItem]);
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v, ...(k === "product" && !isDoor(v) ? { door_model: null, door_glass: null, door_glass_type: null } : {}),
      ...(k === "door_glass" && v !== "ja" ? { door_glass_type: null } : {}),
      ...(k === "door_model" && !v ? { door_glass_type: null, color: COLORS.some((c) => c.id === f.color) ? f.color : null } : {}) }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const prevStep = useRef(step);
  useEffect(() => {
    if (prevStep.current === step) return;
    prevStep.current = step;
    const el = topRef.current;
    if (el && el.getBoundingClientRect().top < 80) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const uploading = uploads.some((u) => !u.path);
  const blocked = (step === 2 && !!postcodeError(form.postcode)) || (step === 4 && uploading);
  const next = () => { if (!blocked) { trackEvent("quote_step", { step, step_name: STEPS[step - 1].short }); setStep((s) => s + 1); } };

  const submit = async () => {
    const errs = validateContact(form);
    setErrors(errs);
    if (Object.keys(errs).length || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/quote-requests", buildPayload(form, uploads, meta.current, isde?.isde_check_id, refItem?.text));
      sessionStorage.removeItem(ISDE_PREFILL_KEY);
      sessionStorage.setItem(QUOTE_DONE_KEY, JSON.stringify({ lead_number: data.lead_number, product_category: form.product || "anders", tracked: false }));
      navigate("/offerte-bedankt");
    } catch (err) {
      toast.error(apiError(err));
      setSubmitting(false);
    }
  };

  const StepBody = [ProductStep, WoningStep, WensenStep, FotoStep, ContactStep][step - 1];
  const last = step === STEPS.length;
  return (
    <div ref={topRef} data-testid="project-advies-form" className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-5 sm:p-8" style={{ boxShadow: "var(--shadow-2)" }}>
      <Progress step={step} />
      {isde && <IsdeLinked isde={isde} onRemove={() => { sessionStorage.removeItem(ISDE_PREFILL_KEY); setIsde(null); }} />}
      {refItem && <ReferenceLinked refItem={refItem} onRemove={clearRef} />}
      <h2 className="text-[22px] font-bold leading-tight text-ink sm:text-[26px]" data-testid="wizard-step-title">{STEPS[step - 1].title}</h2>
      {step === 1 && <p className="mt-2 text-[14.5px] text-slate-500">Kies wat het beste past. Twijfelt u? Kies dan "Ik weet het nog niet".</p>}
      <form className="mt-6" noValidate onSubmit={(e) => { e.preventDefault(); last ? submit() : next(); }}>
        <StepBody form={form} set={set} errors={errors} uploads={uploads} setUploads={setUploads} door={door} systemOptions={systemOptions} />
        <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
          {step > 1 ? (
            <button type="button" onClick={() => setStep((s) => s - 1)} disabled={submitting} data-testid="wizard-back"
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-3 text-[15px] font-semibold text-slate-600 hover:bg-slate-50 hover:text-ink disabled:opacity-40">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Vorige
            </button>
          ) : <span className="hidden sm:block" />}
          <button type="submit" disabled={blocked || submitting} data-testid={last ? "wizard-submit" : "wizard-next"}
            className={`${last ? "btn-cta" : "btn-primary min-h-[52px]"} w-full whitespace-nowrap sm:w-auto disabled:cursor-not-allowed disabled:opacity-50`}>
            {last ? (submitting ? <><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Bezig met versturen…</> : <><Check className="h-5 w-5" aria-hidden="true" /> Aanvraag versturen</>)
              : <>{step === 1 && !form.product ? "Overslaan" : "Volgende"} <ArrowRight className="h-4 w-4" aria-hidden="true" /></>}
          </button>
        </div>
        {last && <p className="mt-3 flex items-center justify-center gap-1.5 sm:justify-end text-[12.5px] text-slate-500"><Lock className="h-3.5 w-3.5" aria-hidden="true" /> Vrijblijvend · uw gegevens worden veilig verwerkt</p>}
      </form>
    </div>
  );
};
